// src/components/reports/ReportReviewModal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, AlertTriangle, Trash2, User, Shield, 
  Maximize2, History, Save, Forward, BookmarkPlus,
  MessageCircle, Ban,
} from 'lucide-react';
import StatusBadge from './StatusBadge';
import ImagePreviewModal from './ImagePreviewModal';
import { reportsService } from '../../services/reportsService';

const MessageContext = ({ context }) => {
  if (!context) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-500">Message Context</h3>
      <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
        {context.messages.map((msg, idx) => (
          <div 
            key={msg._id}
            className={`p-3 ${msg.isFlagged ? 'bg-red-50' : 'bg-white'}`}
          >
            <div className="flex justify-between items-start">
              <span className="text-sm font-medium text-gray-900">
                {msg.sender?.username}
              </span>
              <span className="text-xs text-gray-500">
                {new Date(msg.createdAt).toLocaleTimeString()}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600">{msg.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReportReviewModal = ({ 
  reportId, 
  isOpen, 
  onClose, 
  onAction,
  onReviewComplete 
}) => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [messageContext, setMessageContext] = useState(null);
  const [showContext, setShowContext] = useState(false);



  // Fetch report details
  useEffect(() => {
    const fetchReportDetails = async () => {
      console.log('[ReportReviewModal - Fetching report]', { reportId });
      
      setLoading(true);
      try {
        const data = await reportsService.getReportDetails(reportId);
        console.log('[ReportReviewModal - Report fetched]', data);
        setReport(data);
        setError(null);
      } catch (err) {
        console.error('[ReportReviewModal - Fetch error]', err);
        setError('Failed to load report details');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && reportId) {
      fetchReportDetails();
    }
  }, [isOpen, reportId]); // Remove 'report' from dependencies

  // Separate useEffect for message context
  useEffect(() => {
    const fetchMessageContext = async () => {
      if (report?.contentType === 'Message' && showContext) {
        try {
          const data = await reportsService.getMessageContext(report.contentId);
          setMessageContext(data.context);
        } catch (error) {
          console.error('[ReportReviewModal] Error fetching message context:', error);
        }
      }
    };

    if (report?.contentType === 'Message' && showContext) {
      fetchMessageContext();
    }
  }, [report?.contentType, report?.contentId, showContext]);

  const fetchReportDetails = async () => {
    console.log('[ReportReviewModal - Fetching report]', { reportId });
    
    setLoading(true);
    try {
      const data = await reportsService.getReportDetails(reportId);
      console.log('[ReportReviewModal - Report fetched]', data);
      setReport(data);
      setError(null);
    } catch (err) {
      console.error('[ReportReviewModal - Fetch error]', err);
      setError('Failed to load report details');
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (imageUrl, altText) => {
    console.log('[ReportReviewModal - Opening image preview]', { imageUrl });
    setSelectedImage({ url: imageUrl, alt: altText });
  };

  const handleAction = async (action) => {
    console.log('[ReportReviewModal - Action triggered]', { action });
    setSubmitting(true);
    try {
      if (action === 'dismiss') {
        await reportsService.dismissReport(reportId, notes);
        onReviewComplete?.();
        onClose();
      } else if (action === 'content_removed') {
        await reportsService.reviewReport(reportId, { 
          action: 'content_removed',
          notes: notes || 'Content removed by moderator'
        });
        onReviewComplete?.();
        onClose();
      } else {
        // Handle other actions (e.g., add_note, view_history)
        await onAction(reportId, action);
        fetchReportDetails();
      }
    } catch (err) {
      console.error('[ReportReviewModal - Action Error]', err);
      setError('Failed to perform action');
    } finally {
      setSubmitting(false);
    }
  };

  // const handleMessageAction = async (action) => {
  //   try {
  //     switch (action) {
  //       case 'view_conversation':
  //         // Navigate to conversation view
  //         navigate(`/admin/conversations/${report.metadata.messageContext.conversationId}`);
  //         break;
        
  //       case 'block_user':
  //         await reportsService.reviewFlaggedContent(reportId, {
  //           action: 'block_user',
  //           notes: `User blocked by moderator. Reason: ${report.reason}`
  //         });
  //         onReviewComplete?.();
  //         onClose();
  //         break;
        
  //       case 'delete_messages':
  //         await reportsService.reviewFlaggedContent(reportId, {
  //           action: 'delete_messages',
  //           notes: `All messages deleted by moderator. Reason: ${report.reason}`
  //         });
  //         onReviewComplete?.();
  //         onClose();
  //         break;
          
  //       default:
  //         await handleAction(action);
  //     }
  //   } catch (error) {
  //     console.error('[ReportReviewModal] Message action error:', error);
  //     setError('Failed to perform action');
  //   }
  // };

  const handleViewContent = () => {
    if (!report) return;
    
    switch (report.contentType) {
      case 'goget':
        navigate(`/moderation?type=goget&id=${report.contentId}`);
        break;
      case 'user':
        navigate(`/moderation?type=user&id=${report.userId}`);
        break;
      case 'profile_photo':
        navigate(`/moderation?type=profile_photo&id=${report.userId}`);
        break;
      default:
        console.warn('[ReportReviewModal] Unknown content type:', report.contentType);
    }
    onClose();
  };

  const getAvailableActions = () => {
    if (!report) return [];

    const baseActions = [
      {
        id: 'view_history',
        label: 'View History',
        icon: History,
        available: true
      },
      // Add this new action for resolved reports
      ...(report.status === 'resolved' ? [{
        id: 'unresolve',
        label: 'Unresolve Report',
        icon: AlertTriangle,
        available: true
      }] : []),
      // Only show these for non-resolved reports
      ...(report.status !== 'resolved' ? [
        {
          id: 'save_for_later',
          label: 'Save for Later',
          icon: Save,
          available: report.status === 'pending' || report.status === 'in_review'
        },
        {
          id: 'escalate',
          label: 'Escalate Priority',
          icon: AlertTriangle,
          available: report.status === 'pending' || report.status === 'in_review'
        },
        {
          id: 'forward',
          label: 'Forward to Admin',
          icon: Forward,
          available: report.status === 'pending' || report.status === 'in_review'
        },
        {
          id: 'add_to_batch',
          label: 'Add to Batch',
          icon: BookmarkPlus,
          available: report.status === 'pending' || report.status === 'in_review'
        }
      ] : [])
    ];

    if (report.contentType === 'Message') {
      return [
        ...baseActions,
        {
          id: 'view_conversation',
          label: 'View Conversation',
          icon: MessageCircle,
          available: true
        },
        {
          id: 'block_user',
          label: 'Block User',
          icon: Ban,
          available: report.status !== 'resolved'
        },
        {
          id: 'delete_messages',
          label: 'Delete All Messages',
          icon: Trash2,
          available: report.status !== 'resolved'
        }
      ];
    }

    return baseActions;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const renderImages = () => {
    if (!report) return null;

    if (report.contentType === 'GoGet') {
      return (
        <div className="grid grid-cols-2 gap-4">
          {report.contentId?.images?.item?.url && (
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Item Image</p>
              <div className="relative group">
                <img 
                  src={report.contentId.images.item.url}
                  alt="Reported item"
                  className="w-full h-32 object-cover rounded-lg cursor-pointer"
                  onClick={() => handleImageClick(report.contentId.images.item.url, "Reported item")}
                />
                <button
                  onClick={() => handleImageClick(report.contentId.images.item.url, "Reported item")}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity"
                >
                  <Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>
            </div>
          )}
          {report.contentId?.images?.location?.url && (
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Location Image</p>
              <div className="relative group">
                <img 
                  src={report.contentId.images.location.url}
                  alt="Location"
                  className="w-full h-32 object-cover rounded-lg cursor-pointer"
                  onClick={() => handleImageClick(report.contentId.images.location.url, "Location")}
                />
                <button
                  onClick={() => handleImageClick(report.contentId.images.location.url, "Location")}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity"
                >
                  <Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>
            </div>
          )}
        </div>
      );
    } else if (report.contentType === 'User') {
      return (
        <div className="space-y-2">
          <p className="text-sm text-gray-500">Profile Photo</p>
          {report.contentId?.profilePhotoUrl ? (
            <div className="relative group w-full">
              <img 
                src={report.contentId.profilePhotoUrl}
                alt={`${report.contentId.username}'s profile`}
                className="w-full h-32 object-cover rounded-lg cursor-pointer"
                onClick={() => handleImageClick(
                  report.contentId.profilePhotoUrl, 
                  `${report.contentId.username}'s profile`
                )}
              />
              <button
                onClick={() => handleImageClick(
                  report.contentId.profilePhotoUrl, 
                  `${report.contentId.username}'s profile`
                )}
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity"
              >
                <Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          ) : (
            <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
              <User className="h-12 w-12 text-gray-400" />
            </div>
          )}
        </div>
      );
    }

    return null;
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl max-w-2xl w-full shadow-xl">
          {/* Header */}
          <div className="flex justify-between items-start p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Report Details</h2>
              {report && (
                <p className="mt-1 text-sm text-gray-500">
                  Reported {formatDate(report.createdAt)}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {loading ? (
              <div className="text-center py-4">Loading report details...</div>
            ) : error ? (
              <div className="text-center text-red-600 py-4">{error}</div>
            ) : report ? (
              <>
                {/* Images Section */}
                {renderImages()}

                {/* Primary Actions */}
                {report.status !== 'dismissed' && report.status !== 'resolved' && (
                  <div className="flex space-x-2 mb-6">
                    <button
                      onClick={() => handleAction('dismiss')}
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      <Shield className="h-5 w-5" />
                      <span>Dismiss Report</span>
                    </button>
                    <button
                      onClick={() => handleAction('content_removed')}
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      <Trash2 className="h-5 w-5" />
                      <span>Remove Content</span>
                    </button>
                  </div>
                )}

                {report?.contentType === 'Message' && (
                  <div className="space-y-4">
                    {/* Message Details */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">
                            Flagged Message
                          </h3>
                          <p className="mt-1 text-sm text-gray-600">
                            {report.contentId?.content}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${report.flags?.severity === 'high' ? 'bg-red-100 text-red-800' : 
                            report.flags?.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'}`}
                        >
                          {report.flags?.severity} severity
                        </span>
                      </div>
                    </div>

                    {/* Toggle Context Button */}
                    <button
                      onClick={() => setShowContext(!showContext)}
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <History className="h-4 w-4" />
                      {showContext ? 'Hide Context' : 'Show Context'}
                    </button>

                    {/* Message Context */}
                    {showContext && (
                      <MessageContext context={messageContext} />
                    )}

                    {/* Matched Keywords */}
                    {report.flags?.matchedWords?.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-sm font-medium text-gray-500">
                          Matched Keywords
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {report.flags.matchedWords.map((word, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {word}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}


                {/* Notes Field */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-500">Moderator Notes</h3>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about your review decision..."
                    className="w-full h-24 p-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Report Overview */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Status</h3>
                    <div className="mt-1">
                      <StatusBadge status={report.status} />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Reported Content</h3>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-sm text-gray-900">
                        {report.contentType}: {report.contentId?.itemName || report.contentId?.username}
                      </span>
                      <button 
                        onClick={handleViewContent}
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                      >
                        View Content
                      </button>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Report Details</h3>
                    <div className="mt-1">
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">Reason: </span>
                        {report.reason.replace('_', ' ')}
                      </p>
                      {report.details && (
                        <p className="mt-2 text-sm text-gray-600">{report.details}</p>
                      )}
                    </div>
                  </div>

                  {report.relatedReports?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Related Reports</h3>
                      <p className="mt-1 text-sm text-gray-900">
                        {report.relatedReports.length} other reports for this content
                      </p>
                    </div>
                  )}
                </div>

                {/* Additional Actions */}
                <div className="space-y-2">
                  {getAvailableActions().map((action) => (
                    <button
                      key={action.id}
                      onClick={() => handleAction(action.id)}
                      disabled={!action.available || submitting}
                      className={`w-full px-4 py-2 text-left rounded-lg flex items-center space-x-2
                        ${action.available 
                          ? 'text-gray-700 hover:bg-gray-50' 
                          : 'text-gray-400 cursor-not-allowed'
                        }
                        ${submitting ? 'opacity-50' : ''}
                      `}
                    >
                      <action.icon className="h-5 w-5" />
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage?.url}
        altText={selectedImage?.alt}
      />
    </div>
  );
};

export default ReportReviewModal;